/**
 * ComparisonView - Run parallel topology simulations for comparison
 *
 * Uses the /api/simulation/compare endpoint to run the same scenario
 * across multiple topologies simultaneously and compare results.
 */

import React, { useState } from 'react';
import {
    Box, Typography, Button, Card, Chip, Table, TableBody,
    TableCell, TableHead, TableRow, TextField, Grid, FormControl,
    InputLabel, Select, MenuItem, LinearProgress, Collapse
} from '@mui/material';
import {
    Compare, Add, ExpandMore, ExpandLess, EmojiEvents,
    TrendingUp, TrendingDown, BatteryChargingFull
} from '@mui/icons-material';
import { alpha, keyframes } from '@mui/material/styles';
import { vortxTypography } from '@vortx/shared-ui';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
    RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend
} from 'recharts';

const API_BASE = process.env.REACT_APP_PHD_API_URL || 'http://localhost:5002';
const accentColor = '#9b59b6';

const fadeInUp = keyframes`
    0% { opacity: 0; transform: translateY(20px); }
    100% { opacity: 1; transform: translateY(0); }
`;

const getTopologyColor = (id) => {
    if (id === 1) return '#3498db';
    if (id >= 2 && id <= 4) return '#2ecc71';
    if (id === 5) return '#f39c12';
    if (id === 6) return '#e74c3c';
    if (id === 7) return '#9b59b6';
    if (id >= 8 && id <= 10) return '#5fd4c4';
    if (id === 11) return '#3498db';
    if (id === 12) return '#f39c12';
    return accentColor;
};

// Generate minute-by-minute solar profile
const generateSolarProfile = (minutes, peakKw) => {
    return Array.from({ length: minutes }, (_, i) => {
        const minuteOfDay = i % 1440;
        const hour = minuteOfDay / 60;
        if (hour >= 6 && hour <= 18) {
            const peak = 12;
            const spread = 3;
            const factor = Math.exp(-Math.pow(hour - peak, 2) / (2 * spread * spread));
            return peakKw * factor;
        }
        return 0;
    });
};

// Generate minute-by-minute EV load profile
const generateEvLoadProfile = (minutes, chargerCount, chargerKw, profileType) => {
    const maxLoad = chargerCount * chargerKw;
    return Array.from({ length: minutes }, (_, i) => {
        const minuteOfDay = i % 1440;
        const hour = minuteOfDay / 60;
        let utilization = 0.1;

        switch (profileType) {
            case 'fleet_depot':
                if (hour >= 22 || hour <= 5) utilization = 0.85;
                else if (hour >= 6 && hour <= 8) utilization = 0.3;
                else utilization = 0.1;
                break;
            case 'retail':
                if (hour >= 10 && hour <= 14) utilization = 0.4;
                else if (hour >= 17 && hour <= 21) utilization = 0.7;
                else if (hour >= 14 && hour <= 17) utilization = 0.3;
                break;
            case 'residential':
                if (hour >= 6 && hour <= 8) utilization = 0.5;
                else if (hour >= 18 && hour <= 22) utilization = 0.8;
                else if (hour >= 22 || hour <= 5) utilization = 0.6;
                break;
            case 'commercial':
            default:
                if (hour >= 7 && hour <= 9) utilization = 0.4 + 0.2 * Math.sin((hour - 7) * Math.PI / 2);
                else if (hour >= 9 && hour <= 17) utilization = 0.6;
                else if (hour >= 17 && hour <= 19) utilization = 0.4;
                break;
        }

        const noise = (Math.random() - 0.5) * 0.05;
        return Math.max(0, maxLoad * (utilization + noise));
    });
};

// Generate minute-by-minute AC load profile
const generateAcLoadProfile = (minutes, baseKw = 20) => {
    return Array.from({ length: minutes }, (_, i) => {
        const minuteOfDay = i % 1440;
        const hour = minuteOfDay / 60;
        let factor = 0.3;
        if (hour >= 6 && hour <= 8) factor = 0.3 + 0.5 * ((hour - 6) / 2);
        else if (hour >= 8 && hour <= 18) factor = 0.8 + 0.2 * Math.sin((hour - 8) * Math.PI / 10);
        else if (hour >= 18 && hour <= 22) factor = 0.8 - 0.5 * ((hour - 18) / 4);
        return baseKw * factor;
    });
};

const ComparisonView = ({ comparisonTopologies = [], onRemoveTopology, onNavigateToTopologies }) => {
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [results, setResults] = useState(null);
    const [error, setError] = useState(null);
    const [showConfig, setShowConfig] = useState(true);

    // Equipment configuration (shared across all topologies)
    const [equipment, setEquipment] = useState({
        pv_kw: 100,
        bess_kw: 50,
        bess_kwh: 200,
        charger_count: 4,
        charger_kw: 50
    });
    const [loadProfileType, setLoadProfileType] = useState('commercial');
    const durationMinutes = 1440; // 24 hours

    const handleEquipmentChange = (field, value) => {
        setEquipment(prev => ({
            ...prev,
            [field]: parseFloat(value) || 0
        }));
    };

    const handleRunComparison = async () => {
        if (comparisonTopologies.length < 2) {
            setError('Select at least 2 topologies to compare');
            return;
        }

        setLoading(true);
        setProgress(10);
        setError(null);
        setResults(null);

        try {
            // Generate profiles
            const solar_kw = generateSolarProfile(durationMinutes, equipment.pv_kw);
            const ev_load_kw = generateEvLoadProfile(durationMinutes, equipment.charger_count, equipment.charger_kw, loadProfileType);
            const ac_load_kw = generateAcLoadProfile(durationMinutes, 20);

            setProgress(30);

            const response = await fetch(`${API_BASE}/api/simulation/compare`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    topology_ids: comparisonTopologies.map(t => t.id),
                    equipment,
                    solar_kw,
                    ev_load_kw,
                    ac_load_kw,
                    duration_minutes: durationMinutes,
                }),
            });

            setProgress(80);

            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to run comparison');

            setProgress(100);
            setResults(data);
            setShowConfig(false);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Prepare chart data
    const barChartData = results?.results?.map(r => ({
        name: r.topology_name,
        efficiency: r.efficiency_pct,
        id: r.topology_id,
    })) || [];

    // Radar chart data for multi-metric comparison
    const radarData = results?.results ? [
        {
            metric: 'Efficiency',
            ...Object.fromEntries(results.results.map(r => [r.topology_name, r.efficiency_pct]))
        },
        {
            metric: 'Solar Use',
            ...Object.fromEntries(results.results.map(r => [
                r.topology_name,
                r.total_solar_kwh > 0 ? Math.min(100, (1 - r.total_grid_import_kwh / r.total_ev_kwh) * 100) : 0
            ]))
        },
        {
            metric: 'BESS Util',
            ...Object.fromEntries(results.results.map(r => [
                r.topology_name,
                r.total_bess_throughput_kwh > 0 ? Math.min(100, (r.total_bess_throughput_kwh / equipment.bess_kwh) * 50) : 0
            ]))
        },
        {
            metric: 'Grid Indep',
            ...Object.fromEntries(results.results.map(r => [
                r.topology_name,
                Math.max(0, 100 - (r.total_grid_import_kwh / r.total_ev_kwh) * 100)
            ]))
        },
    ] : [];

    const bestTopology = results?.results?.[0]; // Already sorted by efficiency

    return (
        <Box sx={{ height: '100%', overflow: 'auto', p: 1 }}>
            <Typography sx={{
                fontFamily: vortxTypography.heading,
                fontSize: '1.5rem',
                fontWeight: 600,
                color: accentColor,
                mb: 1,
                textShadow: `0 0 20px ${alpha(accentColor, 0.5)}`,
            }}>
                Topology Comparison
            </Typography>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.8rem',
                color: '#888',
                mb: 3,
            }}>
                Run parallel simulations to compare efficiency across topologies
            </Typography>

            {/* Selected Topologies */}
            <Card sx={{
                bgcolor: 'rgba(30, 30, 30, 0.8)',
                border: '1px solid rgba(255,255,255,0.1)',
                p: 2,
                mb: 3,
            }}>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    color: '#666',
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    mb: 1.5,
                }}>
                    Selected for Comparison ({comparisonTopologies.length}/4)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {comparisonTopologies.map(t => (
                        <Chip
                            key={t.id}
                            label={`#${t.id} ${t.name}`}
                            onDelete={() => onRemoveTopology(t)}
                            sx={{
                                bgcolor: alpha(getTopologyColor(t.id), 0.2),
                                color: getTopologyColor(t.id),
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.7rem',
                                '& .MuiChip-deleteIcon': {
                                    color: getTopologyColor(t.id),
                                    '&:hover': { color: '#fff' },
                                },
                            }}
                        />
                    ))}
                    {comparisonTopologies.length === 0 && (
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            color: '#666',
                        }}>
                            No topologies selected
                        </Typography>
                    )}
                </Box>
                {comparisonTopologies.length < 4 && (
                    <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={onNavigateToTopologies}
                        sx={{
                            color: accentColor,
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            '&:hover': { bgcolor: alpha(accentColor, 0.1) },
                        }}
                    >
                        Add Topologies
                    </Button>
                )}
            </Card>

            {/* Equipment Configuration (Collapsible) */}
            <Card sx={{
                bgcolor: 'rgba(30, 30, 30, 0.8)',
                border: '1px solid rgba(255,255,255,0.1)',
                mb: 3,
                overflow: 'hidden',
            }}>
                <Box
                    onClick={() => setShowConfig(!showConfig)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        p: 2,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                    }}
                >
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        color: '#888',
                        textTransform: 'uppercase',
                        letterSpacing: '0.1em',
                    }}>
                        Equipment Configuration
                    </Typography>
                    {showConfig ? <ExpandLess sx={{ color: '#666' }} /> : <ExpandMore sx={{ color: '#666' }} />}
                </Box>
                <Collapse in={showConfig}>
                    <Box sx={{ p: 2, pt: 0 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Solar PV (kW)"
                                    type="number"
                                    value={equipment.pv_kw}
                                    onChange={(e) => handleEquipmentChange('pv_kw', e.target.value)}
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#f4f1ec',
                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                            '&.Mui-focused fieldset': { borderColor: accentColor },
                                        },
                                        '& .MuiInputLabel-root': { color: '#888' },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <TextField
                                    fullWidth
                                    label="BESS Power (kW)"
                                    type="number"
                                    value={equipment.bess_kw}
                                    onChange={(e) => handleEquipmentChange('bess_kw', e.target.value)}
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#f4f1ec',
                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                            '&.Mui-focused fieldset': { borderColor: accentColor },
                                        },
                                        '& .MuiInputLabel-root': { color: '#888' },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <TextField
                                    fullWidth
                                    label="BESS Capacity (kWh)"
                                    type="number"
                                    value={equipment.bess_kwh}
                                    onChange={(e) => handleEquipmentChange('bess_kwh', e.target.value)}
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#f4f1ec',
                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                            '&.Mui-focused fieldset': { borderColor: accentColor },
                                        },
                                        '& .MuiInputLabel-root': { color: '#888' },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Charger Count"
                                    type="number"
                                    value={equipment.charger_count}
                                    onChange={(e) => handleEquipmentChange('charger_count', e.target.value)}
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#f4f1ec',
                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                            '&.Mui-focused fieldset': { borderColor: accentColor },
                                        },
                                        '& .MuiInputLabel-root': { color: '#888' },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Charger Power (kW)"
                                    type="number"
                                    value={equipment.charger_kw}
                                    onChange={(e) => handleEquipmentChange('charger_kw', e.target.value)}
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#f4f1ec',
                                            '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                            '&.Mui-focused fieldset': { borderColor: accentColor },
                                        },
                                        '& .MuiInputLabel-root': { color: '#888' },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel sx={{ color: '#888' }}>Load Profile</InputLabel>
                                    <Select
                                        value={loadProfileType}
                                        label="Load Profile"
                                        onChange={(e) => setLoadProfileType(e.target.value)}
                                        sx={{
                                            color: '#f4f1ec',
                                            '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor },
                                        }}
                                    >
                                        <MenuItem value="commercial">Commercial</MenuItem>
                                        <MenuItem value="fleet_depot">Fleet Depot</MenuItem>
                                        <MenuItem value="retail">Retail</MenuItem>
                                        <MenuItem value="residential">Residential</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                </Collapse>
            </Card>

            {/* Run Button */}
            <Button
                variant="contained"
                size="large"
                startIcon={<Compare />}
                onClick={handleRunComparison}
                disabled={loading || comparisonTopologies.length < 2}
                fullWidth
                sx={{
                    bgcolor: accentColor,
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.85rem',
                    py: 1.5,
                    mb: 3,
                    '&:hover': {
                        bgcolor: alpha(accentColor, 0.8),
                        boxShadow: `0 0 30px ${alpha(accentColor, 0.5)}`,
                    },
                    '&:disabled': {
                        bgcolor: 'rgba(155, 89, 182, 0.3)',
                        color: 'rgba(255,255,255,0.3)',
                    },
                }}
            >
                {loading ? 'Running Comparison...' : `Compare ${comparisonTopologies.length} Topologies`}
            </Button>

            {/* Progress */}
            {loading && (
                <Box sx={{ mb: 3 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            bgcolor: 'rgba(155, 89, 182, 0.2)',
                            '& .MuiLinearProgress-bar': { bgcolor: accentColor },
                        }}
                    />
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        color: '#888',
                        mt: 1,
                        textAlign: 'center',
                    }}>
                        Running parallel simulations...
                    </Typography>
                </Box>
            )}

            {error && (
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.75rem',
                    color: '#e74c3c',
                    mb: 2,
                }}>
                    Error: {error}
                </Typography>
            )}

            {comparisonTopologies.length < 2 && comparisonTopologies.length > 0 && !results && (
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.75rem',
                    color: '#f39c12',
                    mb: 2,
                }}>
                    Add at least one more topology to compare
                </Typography>
            )}

            {/* Results */}
            {results && (
                <Box sx={{ animation: `${fadeInUp} 0.5s ease-out` }}>
                    {/* Winner Banner */}
                    {bestTopology && (
                        <Card sx={{
                            bgcolor: alpha('#2ecc71', 0.15),
                            border: `1px solid ${alpha('#2ecc71', 0.4)}`,
                            p: 2,
                            mb: 3,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                        }}>
                            <EmojiEvents sx={{ color: '#f1c40f', fontSize: 40 }} />
                            <Box>
                                <Typography sx={{
                                    fontFamily: vortxTypography.heading,
                                    fontSize: '1.1rem',
                                    fontWeight: 700,
                                    color: '#2ecc71',
                                }}>
                                    Best: {bestTopology.topology_name}
                                </Typography>
                                <Typography sx={{
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.75rem',
                                    color: '#888',
                                }}>
                                    {bestTopology.efficiency_pct.toFixed(2)}% efficiency
                                    {results.efficiency_gain_pct > 0 && (
                                        <span style={{ color: '#2ecc71', marginLeft: 8 }}>
                                            +{results.efficiency_gain_pct.toFixed(2)}% vs AC baseline
                                        </span>
                                    )}
                                </Typography>
                            </Box>
                        </Card>
                    )}

                    {/* Efficiency Bar Chart */}
                    <Card sx={{
                        bgcolor: 'rgba(30, 30, 30, 0.8)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        p: 2,
                        mb: 3,
                    }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            color: '#888',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            mb: 2,
                        }}>
                            Efficiency Comparison
                        </Typography>
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={barChartData} margin={{ bottom: 60, left: 10 }}>
                                <XAxis
                                    dataKey="name"
                                    tick={{
                                        fill: '#888',
                                        fontSize: 10,
                                        fontFamily: vortxTypography.mono
                                    }}
                                    angle={-35}
                                    textAnchor="end"
                                    interval={0}
                                    axisLine={{ stroke: '#333' }}
                                    tickLine={{ stroke: '#333' }}
                                />
                                <YAxis
                                    domain={[Math.floor(Math.min(...barChartData.map(d => d.efficiency)) - 2), 100]}
                                    tick={{
                                        fill: '#888',
                                        fontSize: 10,
                                        fontFamily: vortxTypography.mono
                                    }}
                                    axisLine={{ stroke: '#333' }}
                                    tickLine={{ stroke: '#333' }}
                                    label={{
                                        value: 'Efficiency %',
                                        angle: -90,
                                        position: 'insideLeft',
                                        fill: '#888',
                                        fontSize: 10,
                                        fontFamily: vortxTypography.mono
                                    }}
                                />
                                <Tooltip
                                    content={({ active, payload }) => {
                                        if (active && payload && payload.length) {
                                            return (
                                                <Box sx={{
                                                    bgcolor: '#1a1a1a',
                                                    border: '1px solid #333',
                                                    p: 1.5,
                                                    fontFamily: vortxTypography.mono,
                                                }}>
                                                    <Typography sx={{ color: '#f4f1ec', fontSize: '0.8rem', mb: 0.5 }}>
                                                        {payload[0].payload.name}
                                                    </Typography>
                                                    <Typography sx={{ color: getTopologyColor(payload[0].payload.id), fontSize: '0.9rem', fontWeight: 600 }}>
                                                        {payload[0].value.toFixed(2)}% efficiency
                                                    </Typography>
                                                </Box>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                                <Bar dataKey="efficiency" radius={[4, 4, 0, 0]}>
                                    {barChartData.map((entry) => (
                                        <Cell key={entry.name} fill={getTopologyColor(entry.id)} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Card>

                    {/* Detailed Comparison Table */}
                    <Card sx={{
                        bgcolor: 'rgba(30, 30, 30, 0.8)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        overflow: 'hidden',
                        mb: 3,
                    }}>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.3)' }}>
                                        <TableCell sx={{ color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                                            Topology
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                                            Efficiency
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                                            Total Losses
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                                            Grid Import
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                                            BESS Used
                                        </TableCell>
                                        <TableCell align="right" sx={{ color: '#888', fontFamily: vortxTypography.mono, fontSize: '0.65rem', textTransform: 'uppercase' }}>
                                            Rank
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {results.results.map((r, idx) => (
                                        <TableRow key={r.topology_id} sx={{
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.02)' },
                                            bgcolor: idx === 0 ? alpha('#2ecc71', 0.05) : 'transparent',
                                        }}>
                                            <TableCell sx={{
                                                color: getTopologyColor(r.topology_id),
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                            }}>
                                                #{r.topology_id} {r.topology_name}
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                color: '#2ecc71',
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.8rem',
                                                fontWeight: 600,
                                            }}>
                                                {r.efficiency_pct.toFixed(2)}%
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                color: '#e74c3c',
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.75rem',
                                            }}>
                                                {r.total_losses_kwh.toFixed(1)} kWh
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                color: '#3498db',
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.75rem',
                                            }}>
                                                {r.total_grid_import_kwh.toFixed(1)} kWh
                                            </TableCell>
                                            <TableCell align="right" sx={{
                                                color: '#aa88ff',
                                                fontFamily: vortxTypography.mono,
                                                fontSize: '0.75rem',
                                            }}>
                                                {(r.total_bess_throughput_kwh || 0).toFixed(1)} kWh
                                            </TableCell>
                                            <TableCell align="right">
                                                {idx === 0 ? (
                                                    <Chip
                                                        icon={<TrendingUp sx={{ fontSize: 14 }} />}
                                                        label="BEST"
                                                        size="small"
                                                        sx={{
                                                            bgcolor: alpha('#2ecc71', 0.2),
                                                            color: '#2ecc71',
                                                            fontFamily: vortxTypography.mono,
                                                            fontSize: '0.6rem',
                                                            fontWeight: 700,
                                                            height: 20,
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography sx={{
                                                        color: '#888',
                                                        fontFamily: vortxTypography.mono,
                                                        fontSize: '0.75rem',
                                                    }}>
                                                        #{idx + 1}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Card>

                    {/* Loss Breakdown Comparison */}
                    <Card sx={{
                        bgcolor: 'rgba(30, 30, 30, 0.8)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        p: 2,
                    }}>
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            color: '#888',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            mb: 2,
                        }}>
                            Loss Breakdown by Topology
                        </Typography>
                        <Grid container spacing={2}>
                            {results.results.map(r => (
                                <Grid item xs={12} sm={6} md={4} key={r.topology_id}>
                                    <Box sx={{
                                        p: 2,
                                        bgcolor: 'rgba(0,0,0,0.2)',
                                        borderRadius: 1,
                                        border: `1px solid ${alpha(getTopologyColor(r.topology_id), 0.3)}`,
                                    }}>
                                        <Typography sx={{
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.7rem',
                                            color: getTopologyColor(r.topology_id),
                                            fontWeight: 600,
                                            mb: 1,
                                        }}>
                                            #{r.topology_id} {r.topology_name}
                                        </Typography>
                                        {r.loss_breakdown && Object.entries(r.loss_breakdown).map(([key, value]) => (
                                            <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography sx={{
                                                    fontFamily: vortxTypography.mono,
                                                    fontSize: '0.65rem',
                                                    color: '#888',
                                                }}>
                                                    {key.replace(/_/g, ' ').replace('kwh', '')}
                                                </Typography>
                                                <Typography sx={{
                                                    fontFamily: vortxTypography.mono,
                                                    fontSize: '0.65rem',
                                                    color: value > 0 ? '#e74c3c' : '#666',
                                                }}>
                                                    {value.toFixed(2)} kWh
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Card>
                </Box>
            )}
        </Box>
    );
};

export default ComparisonView;
